import { useLingui, Trans } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  Input,
  Label,
  Switch,
  TextArea,
  toast,
} from '@utima/ui';
import { useEffect, useState } from 'react';

import { trpc, type RouterOutputs } from '@/services/trpc';

import { useSkillMutation } from '../hooks/useSkillMutation';

type SkillRow = RouterOutputs['skills']['findAll'][number];

export interface SkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill?: SkillRow;
}

interface FormState {
  name: string;
  description: string;
  content: string;
  availableToAllChats: boolean;
  chatIds: string[];
  isActive: boolean;
}

function initialState(skill?: SkillRow): FormState {
  return {
    name: skill?.name ?? '',
    description: skill?.description ?? '',
    content: skill?.content ?? '',
    availableToAllChats: skill?.availableToAllChats ?? false,
    chatIds: skill?.chatIds ?? [],
    isActive: skill?.isActive ?? true,
  };
}

export function SkillDialog({ open, onOpenChange, skill }: SkillDialogProps) {
  const { t } = useLingui();
  const { create, update } = useSkillMutation();
  const [form, setForm] = useState<FormState>(initialState(skill));

  const isEdit = Boolean(skill);

  useEffect(() => {
    if (open) {
      setForm(initialState(skill));
    }
  }, [open, skill]);

  const { data: chats } = trpc.chats.findAll.useQuery({
    pagination: { pageSize: 1000 },
    fields: ['id', 'name'],
  });

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleChat = (chatId: string) =>
    setForm(prev => ({
      ...prev,
      chatIds: prev.chatIds.includes(chatId)
        ? prev.chatIds.filter(id => id !== chatId)
        : [...prev.chatIds, chatId],
    }));

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.content.trim()) {
      toast.error(t`Name and content are required.`);

      return;
    }

    if (form.description.trim().length < 10) {
      toast.error(t`Description must be at least 10 characters.`);

      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      content: form.content.trim(),
      availableToAllChats: form.availableToAllChats,
      chatIds: form.availableToAllChats ? [] : form.chatIds,
      isActive: form.isActive,
    };

    try {
      await (skill
        ? update.mutateAsync({ id: skill.id, ...payload })
        : create.mutateAsync(payload));

      toast.success(t`Skill saved.`);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t`Failed to save skill.`,
      );
    }
  };

  const chatItems = (chats?.items ?? []) as { id: string; name: string }[];
  const isPending = create.isPending || update.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-[680px] max-w-[95vw]'>
        <Dialog.Header>
          <Dialog.Title>
            {isEdit ? <Trans>Edit Skill</Trans> : <Trans>Add Skill</Trans>}
          </Dialog.Title>
        </Dialog.Header>

        <div className='max-h-[72vh] space-y-5 overflow-y-auto py-2'>
          <div className='space-y-1.5'>
            <Label htmlFor='skill-name'>
              <Trans>Name</Trans>
            </Label>
            <Input
              id='skill-name'
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              placeholder={t`e.g. returns_policy`}
            />
            <p className='text-xs text-muted-fg'>
              <Trans>
                Lowercase letters, digits and underscores; must start with a
                letter.
              </Trans>
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='skill-description'>
              <Trans>Description</Trans>
            </Label>
            <TextArea
              id='skill-description'
              value={form.description}
              onChange={e => setField('description', e.target.value)}
              rows={2}
              placeholder={t`When should the agent load this skill? Shown to the model as the skill catalog.`}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='skill-content'>
              <Trans>Instructions</Trans>
            </Label>
            <TextArea
              id='skill-content'
              value={form.content}
              onChange={e => setField('content', e.target.value)}
              rows={12}
              placeholder={t`Markdown instructions injected when the skill is loaded — procedures, rules, examples.`}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='skill-active'>
              <Trans>Active</Trans>
            </Label>
            <Switch
              id='skill-active'
              checked={form.isActive}
              onCheckedChange={checked => setField('isActive', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='skill-all-chats'>
              <Trans>Available to all chats</Trans>
            </Label>
            <Switch
              id='skill-all-chats'
              checked={form.availableToAllChats}
              onCheckedChange={checked =>
                setField('availableToAllChats', checked)
              }
            />
          </div>

          {!form.availableToAllChats && (
            <div className='space-y-1.5'>
              <Label>
                <Trans>Assigned chats</Trans>
              </Label>
              <div className='max-h-44 space-y-1 overflow-y-auto rounded-radius border border-border p-2'>
                {chatItems.map(chat => (
                  <label
                    key={chat.id}
                    className='flex cursor-pointer items-center gap-2 text-sm'
                  >
                    <input
                      type='checkbox'
                      checked={form.chatIds.includes(chat.id)}
                      onChange={() => toggleChat(chat.id)}
                    />
                    {chat.name}
                  </label>
                ))}
                {chatItems.length === 0 && (
                  <p className='text-xs text-muted-fg'>
                    <Trans>No chats found.</Trans>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <Dialog.Footer>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSubmit} loading={isPending}>
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
